<template>
  <div class="container-nzidoo">
    <page-title :title="$t('menu.list.admins')" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
      <button class="btn btn-primary" @click="openAddModal">
        <i class="mdi mdi-plus"></i>
        <div class="d-none d-md-block">
          {{ $t("action.add") }}
        </div>
      </button>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- Admins Grid -->
    <div class="admin-grid">
      <div v-for="user in filteredUsers" :key="user.id" class="admin-card">
        <div class="admin-card-header">
          <div class="admin-list-status">
            <span v-if="user.is_active == 1" class="badge badge-success">
              {{ $t("status.active") }}
            </span>
            <span v-else class="badge badge-danger">
              {{ $t("status.inactive") }}
            </span>
          </div>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-primary" @click="getUserById(user.id)" :title="$t('action.view')">
              <i class="mdi mdi-eye"></i>
            </button>
            <button v-if="user.is_active == 1" class="btn btn-icon-action btn-warning" @click="disableUser(user.id)"
              :title="$t('action.disable')">
              <i class="mdi mdi-account-off"></i>
            </button>
            <button v-else class="btn btn-icon-action btn-success" @click="enableUser(user.id)"
              :title="$t('action.enable')">
              <i class="mdi mdi-account-check"></i>
            </button>
            <button class="btn btn-icon-action btn-danger" @click="removeUser(user.id)" :title="$t('action.delete')">
              <i class="mdi mdi-delete"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t("main.admin") }}</label>
            <div class="admin-list-name">
              {{ user.lastname }} {{ user.firstname }}
            </div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t("attr.email") }}</label>
            <div class="admin-list-email">
              <i class="mdi mdi-email-outline"></i>
              {{ user.email }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
        <p class="admin-empty-text">
          {{ searchTerm ? ($t("sentence.noResults")) : ($t("main.noAdmins")) }}
        </p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="showDrawer" class="admin-drawer-backdrop" @click="handleClose"></div>
    </transition>

    <!-- Drawer for Adding/Editing -->
    <transition name="drawer">
      <div v-if="showDrawer" class="admin-drawer-container">
        <div class="admin-drawer-header">
          <div>
            <h3 class="admin-drawer-title">
              {{ isEditing ? $t("action.edit") : $t("action.add") }} {{ $t("main.admin") }}
            </h3>
            <p v-if="isEditing" class="admin-drawer-subtitle">
              {{ currentUser.lastname }} {{ currentUser.firstname }}
            </p>
          </div>
          <button class="btn btn-close" @click="handleClose">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <form @submit.prevent="isEditing ? updateUser(currentUser.id) : createUser()">
            <div class="admin-form-group">
              <label class="admin-form-label">{{ $t("attr.lastname") }}</label>
              <input type="text" class="admin-form-input" :placeholder="$t('attr.lastname')"
                v-model="currentUser.lastname" required />
            </div>

            <div class="admin-form-group">
              <label class="admin-form-label">{{ $t("attr.firstname") }}</label>
              <input type="text" class="admin-form-input" :placeholder="$t('attr.firstname')"
                v-model="currentUser.firstname" required />
            </div>

            <div class="admin-form-group">
              <label class="admin-form-label">{{ $t("attr.email") }}</label>
              <input type="email" class="admin-form-input" :placeholder="$t('attr.email')" v-model="currentUser.email"
                required />
            </div>

            <div class="admin-form-group">
              <label class="admin-form-label">{{ $t("attr.password") }}</label>
              <input type="password" class="admin-form-input" :placeholder="$t('attr.password')"
                v-model="currentUser.password" :required="!isEditing" />
              <span v-if="isEditing" class="admin-form-hint">
                {{ $t("main.leaveBlankPassword")  }}
              </span>
            </div>
          </form>
        </div>

        <div class="admin-drawer-footer">
          <button @click="handleClose" class="btn btn-secondary">
            {{ $t("action.cancel") }}
          </button>
          <button @click="isEditing ? updateUser(currentUser.id) : createUser()" :disabled="loader"
            class="btn btn-primary">
            <i class="mdi mdi-check"></i>
            {{ isEditing ? $t("action.update") : $t("action.create") }}
          </button>
        </div>
      </div>
    </transition>

    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";

export default {
  components: { PageTitle,LoadingSpinner },
  name: "manage-admin",

  data() {
    return {
      roles: "ROLE_ADMIN",
      users: [],
      currentUser: {
        lastname: "",
        firstname: "",
        email: "",
        password: ""
      },
      searchTerm: "",
      showDrawer: false,
      isEditing: false,
      loader: false,
    };
  },

  computed: {
    filteredUsers() {
      if (!this.searchTerm) return this.users;

      const term=this.searchTerm.toLowerCase();
      return this.users.filter(user => {
        const fullName=`${user.firstname} ${user.lastname} ${user.email}`.toLowerCase();
        return fullName.includes(term);
      });
    }
  },

  mounted() {
    this.getUsers();
  },

  methods: {

    clearSearch() {
      this.searchTerm="";
    },
    getUsers() {
      const roles="ROLE_ADMIN";
      const self=this;
        this.loader=true;
      this.$store
        .dispatch("getUsers", { roles })
        .then((response) => {
          self.users=response.data;
        this.loader=false;
        })
        .catch((err) => {
          console.error("Error fetching users:", err);
          this.$notify({
            group: "error",
            title: err||"Error fetching user",
            type: "error",
          });
        });
    },

    getUserById(userId) {
      const self=this;
      const roles="ROLE_ADMIN";
      this.$axios({
        url: "admin/list",
        method: "POST",
        data: { userId: userId, roles: roles },
      })
        .then((response) => {
          self.currentUser={ ...response.data };
          self.isEditing=true;
          self.showDrawer=true;
        })
        .catch((err) => {
          console.error("Error fetching user:", err);
          this.$notify({
            group: "error",
            title: err||"Error fetching user",
            type: "error",
          });
        });
    },

    openAddModal() {
      this.currentUser={
        lastname: "",
        firstname: "",
        email: "",
        password: ""
      };
      this.isEditing=false;
      this.showDrawer=true;
    },

    updateUser(userId) {
      const self=this;
      const user=self.currentUser;
      self.loader=true;

      this.$axios({
        url: "admin/update/"+userId,
        method: "POST",
        data: { user },
      })
        .then((res) => {
          self.loader=false;
          self.handleClose();
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error updating user:", err);
          this.$notify({
            group: "error",
            title: err||"Error updating user",
            type: "error",
          });
          self.loader=false;
        });
    },

    createUser() {
      const self=this;
      self.loader=true;

      this.$axios({
        url: "admin/create",
        method: "POST",
        data: self.currentUser,
      })
        .then((res) => {
          self.loader=false;
          self.handleClose();
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error creating user:", err);
          this.$notify({
            group: "error",
            title: err||"Error creating user",
            type: "error",
          });
          self.loader=false;
        });
    },

    disableUser(userId) {
      const self=this;
      this.$axios({
        url: "admin/disable/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error disabling user:", err);
          this.$notify({
            group: "error",
            title: err||"Error disabling user",
            type: "error",
          });
        });
    },

    enableUser(userId) {
      const self=this;
      this.$axios({
        url: "admin/enable/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error enabling user:", err);
          this.$notify({
            group: "error",
            title: err||"Error enabling user",
            type: "error",
          });
        });
    },

    removeUser(userId) {
      const self=this;
      // You might want to add a confirmation dialog here
      this.$axios({
        url: "admin/remove/user/"+userId,
        method: "POST",
      })
        .then((res) => {
          self.getUsers();
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Error removing user:", err);
          this.$notify({
            group: "error",
            title: err||"Error removing user",
            type: "error",
          });
        });
    },

    handleClose() {
      this.showDrawer=false;
      this.currentUser={
        lastname: "",
        firstname: "",
        email: "",
        password: ""
      };
      this.isEditing=false;
    }
  },
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.admin-list-status {
  display: flex;
  gap: 8px;
}

.admin-list-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
}

.admin-list-email {
  font-size: 14px;
  color: var(--gray-600);
  display: flex;
  align-items: center;
  gap: 6px;
}

.admin-list-email i {
  font-size: 16px;
  color: var(--gray-500);
}
</style>